import { StockStatuses } from '@dcxwam/dcx-wam-ui-foundations'
import { FilterOptionsEnum, type GroupedItem } from '../models'
import {
  type ProductDataVariant,
  type Tag
} from '../../../../services/product/product-data'
import { getStockLevelStatus, Stocks, stockStatusMap } from './stock'

export const SORT_PARAM_KEY = 'sort'

export type SortOptionsConfig =
  | {
      sortOptionsBy?: FilterOptionsEnum
      sortByRegex?: string
    }
  | undefined

export const sortFilterOptions =
  ({ sortOptionsBy, sortByRegex }: SortOptionsConfig = {}) =>
  (a: Tag, b: Tag) => {
    const aName = a?.displayName ?? ''
    const bName = b?.displayName ?? ''

    const comparators: Record<FilterOptionsEnum, () => number> = {
      [FilterOptionsEnum.AtoZ]: () => aName.localeCompare(bName),
      [FilterOptionsEnum.ZtoA]: () => bName.localeCompare(aName),
      [FilterOptionsEnum['0to9']]: () =>
        parseFloat(aName) - parseFloat(bName) || 0,
      [FilterOptionsEnum['9to0']]: () =>
        parseFloat(bName) - parseFloat(aName) || 0,
      [FilterOptionsEnum.Custom]: () => {
        if (sortByRegex) {
          const regex = new RegExp(sortByRegex)
          const aMatches = regex.test(aName)
          const bMatches = regex.test(bName)
          if (aMatches !== bMatches) {
            return aMatches ? -1 : 1
          }
        }
        return 0
      }
    }

    return comparators[sortOptionsBy as FilterOptionsEnum]?.() ?? 0
  }

export const sortVariantsBySequenceNumber = (
  a: Partial<ProductDataVariant>,
  b: Partial<ProductDataVariant>
) => {
  const aSequenceNumber = a?.sequenceNumber ?? Infinity
  const bSequenceNumber = b?.sequenceNumber ?? Infinity
  return aSequenceNumber - bSequenceNumber
}

export const sortItemsBySequenceNumber = (a: GroupedItem, b: GroupedItem) => {
  const aMinSequenceNumber = Math.min(
    ...a.variants.map(({ sequenceNumber = Infinity }) => sequenceNumber)
  )
  const bMinSequenceNumber = Math.min(
    ...b.variants.map(({ sequenceNumber = Infinity }) => sequenceNumber)
  )
  return aMinSequenceNumber - bMinSequenceNumber
}

export enum OrderEnum {
  PriceAsc = 'price-asc',
  PriceDesc = 'price-desc'
}

export const sortItemsByPrice =
  (order: `${OrderEnum}` | string) => (a: GroupedItem, b: GroupedItem) => {
    const aMinPrice = Math.min(
      ...a.variants.map((item) =>
        Number(
          item?.price?.priceDiscount?.value ?? item?.price?.grossPrice ?? 0
        )
      )
    )
    const bMinPrice = Math.min(
      ...b.variants.map((item) =>
        Number(
          item?.price?.priceDiscount?.value ?? item?.price?.grossPrice ?? 0
        )
      )
    )
    switch (order) {
      case OrderEnum.PriceAsc:
        return aMinPrice - bMinPrice
      case OrderEnum.PriceDesc:
        return bMinPrice - aMinPrice
      default:
        return 0
    }
  }

export const sortVariantsByStock =
  (stocks?: Stocks) => (a: ProductDataVariant, b: ProductDataVariant) => {
    const aStockLevelStatus = getStockLevelStatus(a, stocks)
    const bStockLevelStatus = getStockLevelStatus(b, stocks)

    const aInventoryStatus = aStockLevelStatus
      ? stockStatusMap[aStockLevelStatus]
      : undefined
    const bInventoryStatus = bStockLevelStatus
      ? stockStatusMap[bStockLevelStatus]
      : undefined

    if (aInventoryStatus === StockStatuses.OutOfStock) {
      return 1
    }

    if (bInventoryStatus === StockStatuses.OutOfStock) {
      return -1
    }

    return 0
  }
